import React, { ReactElement, useCallback, useMemo } from "react";
import { OperationalTargetFormProps } from "./OperationalTargetForm.types";
import { FormSection, FormControl } from "src/ui/components/Forms";
import { TextField, DatePicker } from "@fluentui/react";
import { localize } from "src/l10n";
import { UnstyledButton } from "src/ui";
import Visage2Icon from "src/visage2/Visage2Icon/Visage2Icon";
import { Conditional } from "src/components/Conditional";

function normalizeDate(
    date: Date | undefined,
    type: "start" | "end",
): Date | undefined {
    if (!date) {
        return undefined;
    }

    return type === "start"
        ? new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0)
        : new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
}

/**
 * OperationalTargetForm component
 *
 * @param {OperationalTargetFormProps} props - The component props
 * @returns {ReactElement | null} The rendered component
 */
function OperationalTargetForm({
    model,
    onModelUpdating,
}: OperationalTargetFormProps): ReactElement | null {
    const l10n = useMemo(() => ({
        target: localize("OPS_TARGET_TARGET"),
        focus: localize("OPS_TARGET_FOCUS"),
        startDate: localize("Startdatum"),
        endDate: localize("Slutdatum"),
    }), []);

    const onTargetChanged = useCallback(
        (event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            const newValue = event.currentTarget.value;
            onModelUpdating((prev) => ({ ...prev, target: newValue }));
        }, [onModelUpdating]);

    const onFocusChanged = useCallback(
        (event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            const newValue = event.currentTarget.value;
            onModelUpdating((prev) => ({ ...prev, focus: newValue }));
        }, [onModelUpdating]);

    const onStartDateChanged = useCallback(
        (date?: Date) => {
            onModelUpdating((prev) => ({
                ...prev,
                startDate: normalizeDate(date, "start"),
            }));
        }, [onModelUpdating]);

    const onEndDateChanged = useCallback(
        (date?: Date) => {
            onModelUpdating((prev) => ({
                ...prev,
                endDate: normalizeDate(date, "end"),
            }));
        }, [onModelUpdating]);

    const onClearEndDateClicked = useCallback(() => {
        onModelUpdating((prev) => ({ ...prev, endDate: undefined }));
    }, [onModelUpdating]);

    return (
        <div className="OperationalTargetForm">
            <FormSection>
                <FormControl>
                    <TextField aria-required={true}
                               className="textField"
                               label={l10n.target}
                               name="target"
                               onChange={onTargetChanged}
                               required={true}
                               value={model.target} />
                </FormControl>
            </FormSection>

            <FormSection>
                <FormControl>
                    <TextField aria-required={true}
                               className="textField"
                               label={l10n.focus}
                               name="focus"
                               onChange={onFocusChanged}
                               required={true}
                               value={model.focus} />
                </FormControl>
            </FormSection>

            <FormSection className="OperationalTargetForm-dates">
                <FormControl className="OperationalTargetForm-startDate">
                    <DatePicker ariaLabel={l10n.startDate}
                                firstDayOfWeek={1}
                                isRequired={true}
                                label={l10n.startDate}
                                onSelectDate={onStartDateChanged}
                                placeholder={l10n.startDate}
                                value={model.startDate} />
                </FormControl>
                <FormControl className="OperationalTargetForm-endDate">
                    <DatePicker ariaLabel={l10n.endDate}
                                className="OperationalTargetForm-datePicker"
                                firstDayOfWeek={1}
                                isRequired={false}
                                label={l10n.endDate}
                                onSelectDate={onEndDateChanged}
                                placeholder={l10n.endDate}
                                value={model.endDate} />
                    <Conditional condition={!!model.endDate}>
                        <UnstyledButton className="clear-button"
                                        onClick={onClearEndDateClicked}>
                            <Visage2Icon color="dark-grey"
                                         icon="close-square"
                                         size="big" />
                        </UnstyledButton>
                    </Conditional>
                </FormControl>
            </FormSection>
        </div>
    );
}

export default OperationalTargetForm;

